import { MonitoringEventValueSearchDto } from '@/app/Alert/types';
import axiosClient, { axiosClientAdmin } from '@/lib/auth';
import { Filter, FilterGroup, FilterOperator, SortOrder } from '@/lib/utils';
import { AddressDto } from '@/types/common-types';

export enum EnumAlertStatus {
  'New' = 'New',
  'Weather' = 'Weather',
  'Other' = 'Other',
  'FalsePositive' = 'FalsePositive',
  'PossiblyStaff' = 'PossiblyStaff',
  'ConfirmedStaff' = 'ConfirmedStaff',
  'Intruder' = 'Intruder',
  'Unknown' = 'Unknown',
  'NoThreat' = 'NoThreat',
}

export enum EnumContactEventType {
  'Police' = 'Police',
  'ContactEmail' = 'ContactEmail',
  'ContactSms' = 'ContactSms',
  'ContactSmsReply' = 'ContactSmsReply',
  'ContactCall' = 'ContactCall',
  'MonitoringNotified' = 'MonitoringNotified',
}

export enum EnumVideoProcessor {
  'UmboCv' = 'UmboCv',
  'ThreeDEye' = 'ThreeDEye',
  'Smtp' = 'Smtp',
  'Milestone' = 'Milestone',
  'Camect' = 'Camect',
  'NxWitness' = 'NxWitness',
  'HikVisionNvr' = 'HikVisionNvr',
  'DahuaCamera' = 'DahuaCamera',
  'OpenEye' = 'OpenEye',
}

export enum AssetType {
  'Person' = 'Person',
  'Equipment' = 'Equipment',
  'Plant' = 'Plant',
  'Vehicle' = 'Vehicle',
}

export enum MonitoringEventValueType {
  'SnapshotImage' = 'SnapshotImage',
  'PreviewVideo' = 'PreviewVideo',
  'PersonCount' = 'PersonCount',
  'PersonSeen' = 'PersonSeen',
  'VehicleSeen' = 'VehicleSeen',
  'Unknown' = 'Unknown',
  'AnimalSeen' = 'AnimalSeen',
  'PreviewVideoLink' = 'PreviewVideoLink',
  'PreviewVideoMp4' = 'PreviewVideoMp4',
}

export enum ComponentType {
  'MqttRelay' = 'MqttRelay',
  'Camera' = 'Camera',
  'Router' = 'Router',
  'Siren' = 'Siren',
}

export enum EnumMonitoringEventType {
  'Unknown' = 'Unknown',
  'CameraOnline' = 'CameraOnline',
  'CameraOffline' = 'CameraOffline',
  'CameraTampered' = 'CameraTampered',
  'CameraFailRecord' = 'CameraFailRecord',
  'CameraOverheated' = 'CameraOverheated',
  'CameraMotion' = 'CameraMotion',
  'PersonSeen' = 'PersonSeen',
  'PersonLoitering' = 'PersonLoitering',
  'PersonCount' = 'PersonCount',
  'PersonWallScale' = 'PersonWallScale',
  'VehicleSeen' = 'VehicleSeen',
  'Analytic' = 'Analytic',
}

export interface AlertNoteDto {
  id?: string;
  note?: string;
  author?: string;
  date?: Date;
}

export interface IntruderSnapshot {
  eventValueId?: string;
  topPct?: number;
  leftPct?: number;
  widthPct?: number;
  heightPct?: number;
}

export interface IntruderProfile {
  images?: IntruderSnapshot[];
  number?: number;
  sex?: string;
  age?: string;
  build?: string;
  height?: string;
  ethnicity?: string;
  hair?: string;
  description?: string;
  clothing?: string;
  clothingShoes?: string;
  clothingPants?: string;
  clothingTop?: string;
  otherInfo?: string;
  vehicleType?: string;
  vehicleColor?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  licensePlate?: string;
  occupants?: string;
  vehicleDistinguishingFeatures?: string;
}

export interface IntruderEventData {
  policeId?: string;
  weapons?: string;
  notes?: string;
  intruders?: IntruderProfile[];
  eventOverview?: string;
  closureResult?: string;
  offenceType?: string;
  policeResponded?: boolean;
}

export interface AlertProjectDto {
  name?: string;
  siteDisarmedUntil?: Date;
  newMonitoringAlertsAfterSeconds?: number;
  siteDisarmedBy?: string;
  address?: AddressDto;
}

export interface ContactEventDto {
  occurred?: Date;
  to?: string;
  toId?: string;
  sentBy?: string;
  type?: EnumContactEventType;
  body?: string;
  successful?: boolean;
  parentEventId?: string;
  pending?: boolean;
  id?: string;
}

export interface SmtpRtspDto {
  url?: string;
  timeZone?: string;
  timeFormat?: string;
}

export interface MonitoringEventParsingRuleDto {
  monitoringEventPrefix?: string;
  monitoringEventValueType?: MonitoringEventValueType;
  id?: string;
}

export interface SmtpParsingRuleDto {
  id?: string;
  name?: string;
  datePrefix?: string;
  dateRegex?: string;
  dateFormat?: string;
  timePrefix?: string;
  timeRegex?: string;
  timeFormat?: string;
  channelPrefix?: string;
  channelRegex?: string;
  eventTypePrefix?: string;
  eventTypeRegex?: string;
  eventParsingRules?: MonitoringEventParsingRuleDto[];
}

export interface AssetVideoConfigDto {
  videoProcessor?: EnumVideoProcessor;
  videoProcessorId?: string;
  name?: string;
  description?: string;
  aiPrimer?: string;
  activationZone?: string;
  notes?: string;
  id?: string;
  activationZoneWkt?: string;
  nvrId?: string;
  nvrDeviceName?: string;
  smtpRtsp?: SmtpRtspDto;
  smtpParsingRule?: SmtpParsingRuleDto;
}

export interface AssetComponentDto {
  id?: string;
  type?: ComponentType;
  userName?: string;
}

export interface AssetDto {
  id?: string;
  name?: string;
  externalId?: string;
  groupId?: string;
  companyId?: string;
  projectId?: string;
  hasHardware?: boolean;
  groupName?: string;
  groupType?: AssetType;
  companyName?: string;
  projectName?: string;
  zoneId?: string;
  siteId?: string;
  location?: string;
  lastSensorUpdate?: Date;
  deletedAt?: Date;
  videoProcessorConfigs?: AssetVideoConfigDto[];
  phoneNumber?: string;
  address?: AddressDto;
  serialNumber?: string;
  components?: AssetComponentDto[];
}

export interface MonitoringEventSearchDto {
  id?: string;
  occurred?: Date;
  type?: EnumMonitoringEventType;
  assetId?: string;
  assetDto?: AssetDto;
  assetName?: string;
  projectId?: string;
  companyId?: string;
  videoProcessorId?: string;
  videoProcessorDescription?: string;
  values?: MonitoringEventValueSearchDto[];
  projectName?: string;
  companyName?: string;
  alertId?: string;
  alertStart?: Date;
  alertClosedAt?: Date;
  videoProcessor?: EnumVideoProcessor;
  activationZone?: string;
  alertNumber?: string;
}

export interface MonitoringAlertDto {
  alertStart?: Date;
  alertClosedAt?: Date;
  videoProcessor?: EnumVideoProcessor;
  activationZone?: string;
  alertNumber?: string;
}

export interface MonitoringAlertDto {
  id?: string;
  projectId?: string;
  projectName?: string;
  notified?: boolean;
  notifiedAt?: Date;
  lastEvent?: Date;
  actionedBy?: string;
  disarmedAt?: Date;
  disarmedBy?: string;
  actionedAt?: Date;
  closedAt?: Date;
  recentNotes?: AlertNoteDto[];
  occurred?: Date;
  companyId?: string;
  companyName?: string;
  status?: EnumAlertStatus;
  project?: AlertProjectDto;
  contactEvents?: ContactEventDto[];
  alertId?: string;
  events?: MonitoringEventSearchDto[];
  intruderEventData?: IntruderEventData;
}

export interface MonitoringAlertFetched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  alert?: MonitoringAlertDto;
}

export const getAlert = async (alertId: string): Promise<MonitoringAlertDto> => {
  try {
    const response = await axiosClientAdmin.get<MonitoringAlertFetched>(
      `/Monitoring/alerts/${alertId}`
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data.alert!;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to get alert');
    }
    throw new Error('Failed to get alert');
  }
};

export interface MonitoringEventsSearched {
  rows: MonitoringEventSearchDto[];
  totalRows: number;
}

export interface MonitoringAlertsSearched {
  rows: MonitoringAlertDto[];
  totalRows: number;
}

export const getMonitoringEvents = async (
  pageNumber?: number,
  pageSize?: number,
  sortBy?: string,
  sortDir?: SortOrder,
  filterOperator?: FilterOperator,
  filterFilters?: Filter[],
  filterFilterGroups?: FilterGroup[],
  enabled?: boolean
): Promise<MonitoringEventsSearched> => {
  try {
    const response = await axiosClientAdmin.get('/Monitoring/event/search', {
      params: {
        PageNumber: pageNumber,
        PageSize: pageSize,
        SortBy: sortBy,
        SortDir: sortDir,
        'Filter.Operator': filterOperator,
        'Filter.Filters': filterFilters,
        'Filter.FilterGroups': filterFilterGroups,
        Enabled: enabled,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to fetch monitoring events:', error);
    throw new Error('Failed to fetch monitoring events');
  }
};

export interface ImageUrlDto {
  url?: string;
}

export const getCameraSnapshotMonitoring = async (cameraId: string): Promise<ImageUrlDto> => {
  try {
    const response = await axiosClientAdmin.get<ImageUrlDto>(
      `/Monitoring/camera/${cameraId}/snapshot`
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to get alert');
    }
    throw new Error('Failed to get alert');
  }
};

export interface CameraZoneForm {
  wkt?: string;
}

export interface ActivationZoneSet {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
}

export const postSetCameraActivationZoneMonitoring = async (
  cameraId: string,
  body?: CameraZoneForm
): Promise<ActivationZoneSet> => {
  try {
    const response = await axiosClientAdmin.post<ActivationZoneSet>(
      `/Monitoring/camera/${cameraId}/zone`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to set activation zone');
    }
    throw new Error('Failed to set activation zone');
  }
};

export interface Steps {
  stepIndex: number;
  completedAt?: string;
  note: string | null;
  actioned: boolean;
}

export interface AlertDispositionWorkflowHistory {
  statusHistories?: Partial<Record<EnumAlertStatus, Steps[]>>;
}

export interface WorkflowHistoryResponse {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
}

export const updateAlertWorkflowHistory = async (
  alertId: string,
  body: AlertDispositionWorkflowHistory
): Promise<WorkflowHistoryResponse> => {
  try {
    const response = await axiosClient.post<WorkflowHistoryResponse>(
      `/AlertMonitoring/${alertId}/workflow-history`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to update workflow history');
    }
    throw new Error('Failed to update workflow history');
  }
};

export interface AddAlertNoteDto {
  note?: string;
  author?: string;
  nonActioningNote?: boolean;
}

export interface AlertNoteAdded {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
}

export interface DisarmDto {
  alertId?: string;
  disarmedBy?: string;
  reason?: string;
}

export interface AlertDisarmed {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  alert?: MonitoringAlertDto;
}

export interface AlertStatusDto {
  status?: EnumAlertStatus;
  setBy?: string;
}

export interface AlertStatusSet {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  alert?: MonitoringAlertDto;
}

export interface CloseAlertDto {
  alertId?: string;
  closingAuthor?: string;
  resolution?: string;
}

export interface AlertClosed {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  alert?: MonitoringAlertDto;
}

export interface AlertIntruderInfoSet {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
}

export const addAlertNote = async (
  alertId: string,
  param: AddAlertNoteDto
): Promise<AlertNoteAdded> => {
  try {
    const response = await axiosClientAdmin.post<AlertNoteAdded>(
      `/Monitoring/alerts/${alertId}/note`,
      param
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to add alert note');
    }
    throw new Error('Failed to add alert note');
  }
};

export const disarmAlert = async (alertId: string, body?: DisarmDto): Promise<AlertDisarmed> => {
  try {
    const response = await axiosClientAdmin.post<AlertDisarmed>(
      `/Monitoring/alerts/${alertId}/disarm`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to disarm alert');
    }
    throw new Error('Failed to disarm alert');
  }
};

export const updateAlertStatus = async (
  alertId: string,
  body?: AlertStatusDto
): Promise<AlertStatusSet> => {
  try {
    const response = await axiosClientAdmin.post<AlertStatusSet>(
      `/Monitoring/alerts/${alertId}/status`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to update alert status');
    }
    throw new Error('Failed to update alert status');
  }
};

export const closeAlert = async (alertId: string, body?: CloseAlertDto): Promise<AlertClosed> => {
  try {
    const response = await axiosClientAdmin.post<AlertClosed>(
      `/Monitoring/alerts/${alertId}/close`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to close alert');
    }
    throw new Error('Failed to close alert');
  }
};

/** @deprecated */
export const searchAlerts = async ({
  pageNumber = 0,
  pageSize = 10,
  sortBy,
  sortDir,
  filterOperator,
  filterFilters,
  filterFilterGroups,
}: {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: SortOrder;
  filterOperator?: FilterOperator;
  filterFilters?: Filter[];
  filterFilterGroups?: FilterGroup[];
} = {}): Promise<MonitoringAlertsSearched> => {
  try {
    const response = await axiosClientAdmin.get<MonitoringAlertsSearched>(
      '/Monitoring/alerts/search',
      {
        params: {
          PageNumber: pageNumber,
          PageSize: pageSize,
          SortBy: sortBy,
          SortDir: sortDir,
          'Filter.Operator': filterOperator,
          'Filter.Filters': filterFilters,
          'Filter.FilterGroups': filterFilterGroups,
        },
      }
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to search alerts');
    }
    throw new Error('Failed to search alerts');
  }
};

export const updateIntruderDetails = async (
  alertId: string,
  body: IntruderEventData
): Promise<AlertIntruderInfoSet> => {
  try {
    const response = await axiosClientAdmin.post<AlertIntruderInfoSet>(
      `/Monitoring/alerts/${alertId}/intruder`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error && error.response) {
      const errorResponse = error.response as { data?: { message?: string } };
      throw new Error(errorResponse.data?.message || 'Failed to update intruder info');
    }
    throw new Error('Failed to update intruder info');
  }
};
